"use client";

import { StediEligibilityActions, StediEligibilityCard } from "@/src/components/tasks/stedi-eligibility-summary";
import { useUser } from "@/src/context/user-context";
import { useToast } from "@/src/hooks/use-toast";
import {
  useRecheckReferralEligibility,
  useStediEligibilityCheck,
} from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { canWriteTasks } from "@/src/lib/rbac";
import type { Coverage } from "@/src/models/coverage";

interface ReferralEligibilityCardProps {
  referralId: string;
  taskId: string;
  primaryCoverage: Coverage | null;
  secondaryCoverage: Coverage | null;
}

export function ReferralEligibilityCard({
  primaryCoverage,
  secondaryCoverage,
  ...context
}: ReferralEligibilityCardProps) {
  return (
    <div className="space-y-4">
      <CoverageEligibilityCard {...context} coverage={primaryCoverage} />
      {secondaryCoverage && <CoverageEligibilityCard {...context} coverage={secondaryCoverage} />}
    </div>
  );
}

function CoverageEligibilityCard({
  referralId,
  taskId,
  coverage,
}: {
  referralId: string;
  taskId: string;
  coverage: Coverage | null;
}) {
  const { roles } = useUser();
  const { toast } = useToast();
  const { eligibilityCheck, isLoading } = useStediEligibilityCheck(referralId, coverage);
  const recheckMutation = useRecheckReferralEligibility(taskId, referralId);

  const handleRecheck = () => {
    recheckMutation.mutate(coverage, {
      onSuccess: (data) => {
        if (data.skipped) {
          toast({
            title: "Eligibility check skipped",
            description: data.reason
              ? `Reason: ${data.reason.replaceAll("_", " ")}`
              : "No matching eligibility details found for this referral.",
            variant: "destructive",
          });

          return;
        }

        toast({
          title: "Eligibility check successful",
          description: "Insurance eligibility check was refreshed.",
        });
      },
      onError: (error) => {
        toast({
          title: "Eligibility check failed",
          description: error instanceof Error ? error.message : "An unexpected error occurred.",
          variant: "destructive",
        });
      },
    });
  };

  return (
    <StediEligibilityCard
      check={eligibilityCheck}
      title={coverage?.order === 2 ? "Secondary Insurance Eligibility" : "Insurance Eligibility"}
      actions={
        coverage && canWriteTasks(roles) && !isLoading ? (
          <StediEligibilityActions
            check={eligibilityCheck}
            onRecheck={handleRecheck}
            isRechecking={recheckMutation.isPending}
          />
        ) : undefined
      }
    />
  );
}
